/**
 * Title hygiene — the ONE rule for "this title reads like an internal or test
 * name" and the ONE fork default title.
 *
 * The 2026-11-04 owner report: public games showed working names in-game
 * ('Space Invaders — Player Proof V1', 'Beacon Sequence Delivery QA',
 * 'Blue Max (Composed 5)', forks 'Glyph Garden V1 V1'). The pattern below is
 * the one migration 0136 used to find such titles for its cleanup; it now
 * warns the creator BEFORE the public flip. It never blocks (owner Decision
 * 267): a game may legitimately be called "Proof of Concept".
 */

/** Words that mark a working/internal name, as whole tokens (so "Contest" or
 *  "Regenerate" don't match), plus a version-number token like "V2". */
export const INTERNAL_TITLE_PATTERN = /(^|[^a-z])(qa|test|proof|composed|regen|v[0-9]+)([^a-z]|$)/i;

/** The creator-facing warning for a title that looks internal, or null.
 *  Advisory only: the caller shows it next to the publish action. */
export function titleHygieneWarning(title: string): string | null {
  const match = INTERNAL_TITLE_PATTERN.exec(title);
  if (!match) return null;
  return `“${title}” looks like a working or test name (“${match[2]}”). Players see this title in the game and the gallery — rename it first if it isn’t the name you want.`;
}

/** Max creation title length (updateCreation's cap). */
export const TITLE_MAX_LEN = 80;

/** A trailing fork marker a parent title may already carry: our own
 *  " Remix" / " Remix 2" or the retired " V<n>" default. */
const FORK_SUFFIX = /\s+(?:remix(?:\s+\d+)?|v\d+)$/i;

/** The remix number a title already carries: 1 for "… Remix", k for
 *  "… Remix k", 0 otherwise. */
function remixNumber(title: string): number {
  const match = /\sremix(?:\s+(\d+))?$/i.exec(title.trim());
  return match ? Number(match[1] ?? 1) : 0;
}

/**
 * The default title a new fork is born with: "<parent> Remix" for the first
 * fork of a parent, "<parent> Remix <n>" after that, so two cards never read
 * identically (owner decision 2026-07-13). A parent that is itself a fork
 * loses its marker and the number continues past it, so a fork of
 * "Glyph Garden Remix" is "Glyph Garden Remix 2", never "Glyph Garden V1 V1"
 * and never its parent's own name. The forker can rename any time.
 */
export function forkDefaultTitle(parentTitle: string, priorForkCount: number): string {
  let base = parentTitle.trim();
  const parentRemix = remixNumber(base);
  while (FORK_SUFFIX.test(base)) base = base.replace(FORK_SUFFIX, '');
  const n = Math.max(priorForkCount + 1, parentRemix + 1);
  const suffix = n === 1 ? ' Remix' : ` Remix ${n}`;
  return `${(base || 'Game').slice(0, TITLE_MAX_LEN - suffix.length).trimEnd()}${suffix}`;
}
